/*
 * Cookie / local-storage notice banner. Appears on first visit and
 * dismisses to localStorage so it doesn't pester returning buyers.
 *
 * Used by every public tool entry point and the SWA shell. Lightweight
 * — no portal, no animation library, just a fixed div that respects
 * prefers-reduced-motion.
 *
 * "Privacy & data use" link points to the canonical privacy URL. On
 * Squarespace builds where the SPA shell isn't running, that link
 * needs to point at the SWA-hosted privacy page (pass `privacyHref`).
 */
import { useEffect, useState } from 'react';
import './privacy.css';

const DISMISSED_KEY = 'motr_privacy_dismissed_v1';

interface PrivacyBannerProps {
  /** Where the "Privacy & data use" link points. Defaults to /privacy. */
  privacyHref?: string;
}

export function PrivacyBanner({ privacyHref = '/privacy' }: PrivacyBannerProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(DISMISSED_KEY)) setShow(true);
    } catch {
      // Storage disabled — show the banner every time. Defensible.
      setShow(true);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    } catch {
      /* ok — banner just keeps showing */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="privacy-banner"
      role="region"
      aria-label="Privacy and data use notice"
    >
      <p>
        These tools save your progress in your browser's local storage and send
        anonymous diagnostic data to our analytics. We don't ask for your name,
        email, or company.{' '}
        <a href={privacyHref} target="_blank" rel="noreferrer noopener">
          Read the full notice
        </a>
        .
      </p>
      <div className="privacy-banner-actions">
        <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={dismiss}>
          Got it
        </button>
      </div>
    </div>
  );
}

/**
 * Footer link block used at the bottom of every public tool.
 */
export function PrivacyLinkFooter({ privacyHref = '/privacy' }: PrivacyBannerProps) {
  return (
    <div className="privacy-link-footer no-print">
      <a href={privacyHref} target="_blank" rel="noreferrer noopener">
        Privacy &amp; data use
      </a>
    </div>
  );
}
